import { useEffect, useState } from "react";
import { BellOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  isPushSupported,
  requestPermissionAndSubscribe,
} from "../lib/pushManager";

/**
 * Inline reminder shown at the top of /chat. Context-relevant version of the
 * dashboard banner — appears whenever the user has chat open but hasn't
 * granted permission yet, regardless of whether they dismissed the dashboard
 * prompt (the chat case is more compelling).
 *
 * Hides itself silently when permission is 'granted' or unsupported.
 */
export const ChatPushReminder = () => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (Notification.permission === "granted") return;
    if (Notification.permission === "denied") return; // can't re-prompt anyway
    setVisible(true);
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    setBusy(true);
    const status = await requestPermissionAndSubscribe();
    setBusy(false);
    if (status === "granted") {
      toast.success("Notifications on — you'll be pinged for new messages");
      setVisible(false);
    } else if (status === "denied") {
      toast.error("Permission denied — you can change this in your browser settings.");
      setVisible(false);
    } else {
      toast.error("Couldn't enable. Try again from Profile.");
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 px-4 py-2.5 mb-2 rounded-md border border-amber-400/20 bg-amber-400/[0.06]"
      data-testid="chat-push-reminder"
    >
      <BellOff className="w-4 h-4 text-amber-400 flex-shrink-0" />
      <span className="text-xs sm:text-sm text-amber-200 flex-1 min-w-0 break-words">
        You won't get notified about new messages — enable browser notifications to fix this.
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={handleEnable}
        disabled={busy}
        className="h-7 text-xs border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
        data-testid="chat-push-reminder-enable"
      >
        {busy ? "Enabling…" : "Enable"}
      </Button>
      <button
        type="button"
        onClick={() => setVisible(false)}
        className="text-amber-200/70 hover:text-amber-200 text-xs"
        aria-label="Hide for this session"
        data-testid="chat-push-reminder-dismiss"
      >
        ×
      </button>
    </div>
  );
};
